import { useEffect, useState } from "react";
import { api, fmtMoney, fmtDate, API } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Check, X, Wallet, Upload, ArrowDownToLine, ArrowUpFromLine, Scale, FileSpreadsheet, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function Reconciliation() {
    const [accounts, setAccounts] = useState([]);
    const [accountId, setAccountId] = useState("");
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [importOpen, setImportOpen] = useState(false);
    const [importBusy, setImportBusy] = useState(false);
    const [aiSugg, setAiSugg] = useState({}); // txn_id -> {loading, data}

    useEffect(() => { api.get("/bank-accounts").then(({ data }) => { setAccounts(data); if (data[0]) setAccountId(data[0].id); }); }, []);

    const load = async () => {
        if (!accountId) return;
        setLoading(true);
        const { data } = await api.get("/bank-transactions", { params: { bank_account_id: accountId } });
        setTransactions(data); setLoading(false);
    };
    useEffect(() => { load(); }, [accountId]);

    const account = accounts.find((a) => a.id === accountId);
    const ccy = account?.currency || "INR";

    const reconciled = transactions.filter((t) => t.is_reconciled);
    const unreconciled = transactions.filter((t) => !t.is_reconciled);
    const recBalance = reconciled.reduce((s, t) => s + t.amount, 0);
    const unrecBalance = unreconciled.reduce((s, t) => s + t.amount, 0);
    const diff = (account?.current_balance || 0) - recBalance;

    const reconcile = async (id) => {
        await api.post(`/bank-transactions/${id}/reconcile`, {});
        toast.success("Reconciled");
        await load();
    };
    const unreconcile = async (id) => {
        await api.post(`/bank-transactions/${id}/unreconcile`);
        await load();
    };

    const aiCategorize = async (t) => {
        setAiSugg((s) => ({ ...s, [t.id]: { loading: true } }));
        try {
            const { data } = await api.post(`/banking/ai-categorize`, { transaction_id: t.id });
            setAiSugg((s) => ({ ...s, [t.id]: { loading: false, data } }));
        } catch (e) {
            setAiSugg((s) => ({ ...s, [t.id]: { loading: false, error: e?.response?.data?.detail || "Failed" } }));
        }
    };

    const onImport = async (e) => {
        e.preventDefault();
        const file = e.target.elements.file.files[0];
        if (!file) return toast.error("Pick a CSV file");
        setImportBusy(true);
        const fd = new FormData();
        fd.append("file", file);
        fd.append("bank_account_id", accountId);
        try {
            const token = localStorage.getItem("vyastra_token");
            const res = await fetch(`${API}/bank-transactions/import`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
            if (!res.ok) throw new Error();
            const json = await res.json();
            toast.success(`Imported ${json.imported} transactions${json.skipped ? `, skipped ${json.skipped}` : ""}`);
            setImportOpen(false);
            await load();
        } catch (err) { toast.error("Import failed"); }
        finally { setImportBusy(false); }
    };

    return (
        <>
            <PageHeader kicker="BANKING" title="Reconciliation" subtitle="Match bank statement lines to your books, line by line."
                actions={
                    <>
                        <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="rec-import"><Upload className="w-4 h-4 mr-1.5" />Import CSV</Button>
                        <Select value={accountId} onValueChange={setAccountId}>
                            <SelectTrigger className="w-[260px]" data-testid="rec-account"><SelectValue placeholder="Pick an account" /></SelectTrigger>
                            <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.currency})</SelectItem>)}</SelectContent>
                        </Select>
                    </>
                }
            />
            <PageBody>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                    <Tile kicker="Account balance" label="Book balance" value={account?.current_balance || 0} ccy={ccy} icon={Wallet} accent="info" />
                    <Tile kicker="Reconciled" label={`${reconciled.length} txns`} value={recBalance} ccy={ccy} icon={Check} accent="positive" />
                    <Tile kicker="Unreconciled" label={`${unreconciled.length} txns`} value={unrecBalance} ccy={ccy} icon={ArrowUpFromLine} accent="warning" />
                    <Tile kicker="Difference" label="To reconcile" value={diff} ccy={ccy} icon={Scale} accent={Math.abs(diff) < 0.01 ? "positive" : "rose"} />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <Panel title="Statement (unreconciled)" subtitle="Tap reconcile to match.">
                        {unreconciled.length === 0 ? <div className="p-8 text-center text-[12px] text-muted-foreground">All transactions reconciled.</div> :
                            <table className="fin-table">
                                <thead><tr><th>Date</th><th>Description</th><th className="num">Amount</th><th></th></tr></thead>
                                <tbody>
                                    {unreconciled.map((t) => (
                                        <tr key={t.id} data-testid={`rec-unrec-${t.id}`}>
                                            <td className="text-[12px] text-muted-foreground">{fmtDate(t.date)}</td>
                                            <td>
                                                <div className="font-medium">{t.description}</div>
                                                <div className="text-[11px] text-muted-foreground font-mono">{t.reference}</div>
                                                {aiSugg[t.id]?.loading && (
                                                    <div className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><Sparkles className="w-3 h-3 animate-pulse" />Categorising…</div>
                                                )}
                                                {aiSugg[t.id]?.data?.suggestions?.[0] && (
                                                    <div className="text-[11px] mt-1 px-2 py-1 rounded bg-gold/10 border border-gold/20 inline-flex items-center gap-1.5" data-testid={`ai-sugg-${t.id}`}>
                                                        <Sparkles className="w-3 h-3 text-gold" />
                                                        <span className="text-gold font-medium">{aiSugg[t.id].data.suggestions[0].account_name}</span>
                                                        <span className="text-muted-foreground">· {(aiSugg[t.id].data.suggestions[0].confidence * 100).toFixed(0)}% conf</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className={cn("num", t.amount < 0 ? "text-destructive" : "text-positive")}>{fmtMoney(t.amount, ccy)}</td>
                                            <td className="space-x-1">
                                                <Button size="sm" variant="ghost" onClick={() => aiCategorize(t)} disabled={aiSugg[t.id]?.loading} data-testid={`rec-ai-${t.id}`} title="Suggest account using AI">
                                                    <Sparkles className="w-3.5 h-3.5" />
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => reconcile(t.id)} data-testid={`rec-mark-${t.id}`}><Check className="w-3.5 h-3.5 mr-1" />Match</Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>}
                    </Panel>
                    <Panel title="Reconciled" subtitle="Already matched.">
                        {reconciled.length === 0 ? <div className="p-8 text-center text-[12px] text-muted-foreground">Nothing reconciled yet.</div> :
                            <table className="fin-table">
                                <thead><tr><th>Date</th><th>Description</th><th className="num">Amount</th><th></th></tr></thead>
                                <tbody>
                                    {reconciled.map((t) => (
                                        <tr key={t.id}>
                                            <td className="text-[12px] text-muted-foreground">{fmtDate(t.date)}</td>
                                            <td><div className="font-medium">{t.description}</div></td>
                                            <td className={cn("num", t.amount < 0 ? "text-destructive" : "text-positive")}>{fmtMoney(t.amount, ccy)}</td>
                                            <td><Button size="sm" variant="ghost" onClick={() => unreconcile(t.id)}><X className="w-3.5 h-3.5" /></Button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>}
                    </Panel>
                </div>
            </PageBody>

            <Sheet open={importOpen} onOpenChange={setImportOpen}>
                <SheetContent side="right" className="w-full sm:max-w-[460px] p-0 flex flex-col">
                    <SheetHeader className="border-b border-border px-6 py-4"><SheetTitle className="text-xl font-semibold tracking-tight">Import bank statement</SheetTitle></SheetHeader>
                    <form className="flex-1 overflow-y-auto p-6 space-y-4" onSubmit={onImport}>
                        <div className="rounded-md border border-border bg-muted/30 p-4 text-[12.5px] text-muted-foreground">
                            <div className="flex items-center gap-2 mb-2"><FileSpreadsheet className="w-4 h-4 text-gold" /><span className="font-semibold text-foreground">CSV format</span></div>
                            <p>Required columns: <code className="font-mono text-[11.5px]">date</code>, <code className="font-mono text-[11.5px]">description</code>, <code className="font-mono text-[11.5px]">amount</code>.</p>
                            <p className="mt-1.5">Optional: <code className="font-mono text-[11.5px]">reference</code>. Negative amounts = withdrawals.</p>
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">Bank statement file</label>
                            <input type="file" name="file" accept=".csv" required data-testid="rec-import-file"
                                className="mt-2 block w-full text-[13px] file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-[12px] file:font-semibold file:bg-foreground file:text-background hover:file:bg-foreground/90" />
                        </div>
                        <Button type="submit" disabled={importBusy} className="w-full" data-testid="rec-import-submit">{importBusy ? "Importing…" : "Import transactions"}</Button>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
}

function Tile({ kicker, label, value, ccy, icon: Icon, accent }) {
    return (
        <div className="bg-card border border-border rounded-lg p-5">
            <div className="flex items-start justify-between mb-3">
                <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-muted-foreground">{kicker}</div>
                {Icon && (
                    <div className={cn("w-9 h-9 rounded-md flex items-center justify-center shrink-0", `tint-${accent}`)}>
                        <Icon className="w-[18px] h-[18px]" strokeWidth={1.7} />
                    </div>
                )}
            </div>
            <div className="text-[26px] font-semibold tracking-tight kpi-num">{fmtMoney(value, ccy)}</div>
            <div className="text-[11.5px] text-muted-foreground mt-1.5">{label}</div>
        </div>
    );
}

function Panel({ title, subtitle, children }) {
    return (
        <div className="bg-card border border-border rounded-md overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
                <div className="font-semibold text-[14px]">{title}</div>
                <div className="text-[11.5px] text-muted-foreground mt-0.5">{subtitle}</div>
            </div>
            {children}
        </div>
    );
}
